import { Components } from 'formiojs';
import FieldComponent from 'formiojs/components/_classes/field/Field';

export class RichTextEditorComponent extends FieldComponent {
  component: any;
  addEventListener: any;
  refs: any;
  dataValue: any;
  triggerChange: any;
  static schema(...extend: any[]) {
    return FieldComponent.schema(
      {
        type: 'richtexteditor',
        label: 'Rich Text Editor',
        key: 'richtext',
        input: true,
        showToolbar: true,
        allowImages: false,
        maxLength: 5000
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: 'Rich Text Editor',
      group: 'basic',
      icon: 'edit',
      weight: 85,
      schema: RichTextEditorComponent.schema()
    };
  }

  static editForm() {
    const editForm = FieldComponent.editForm();

    editForm.components.push({
      type: 'checkbox',
      label: 'Show Toolbar',
      key: 'showToolbar',
      input: true,
      weight: 50
    });

    editForm.components.push({
      type: 'checkbox',
      label: 'Allow Images',
      key: 'allowImages',
      input: true,
      weight: 60
    });

    editForm.components.push({
      type: 'number',
      label: 'Max Length',
      key: 'maxLength',
      input: true,
      defaultValue: 5000,
      weight: 70
    });

    return editForm;
  }

  render() {
    const showToolbar = this.component.showToolbar !== false;
    const maxLength = this.component.maxLength || 5000;
    
    return super.render(`
      <div class="rich-text-container">
        ${showToolbar ? `
          <div ref="toolbar" class="rte-toolbar" style="
            border: 1px solid #ddd;
            border-bottom: none;
            background: #f8f9fa;
            padding: 8px;
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
          ">
            <button type="button" class="rte-btn" data-command="bold" title="Bold" style="
              padding: 4px 8px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 3px;
            "><b>B</b></button>
            <button type="button" class="rte-btn" data-command="italic" title="Italic" style="
              padding: 4px 8px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 3px;
            "><i>I</i></button>
            <button type="button" class="rte-btn" data-command="underline" title="Underline" style="
              padding: 4px 8px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 3px;
            "><u>U</u></button>
            <div style="width: 1px; height: 20px; background: #ccc; margin: 0 4px;"></div>
            <button type="button" class="rte-btn" data-command="insertUnorderedList" title="Bullet List" style="
              padding: 4px 8px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 3px;
            ">•</button>
            <button type="button" class="rte-btn" data-command="insertOrderedList" title="Numbered List" style="
              padding: 4px 8px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 3px;
            ">1.</button>
          </div>
        ` : ''}
        <div 
          ref="editor" 
          contenteditable="true" 
          class="rte-editor"
          style="
            border: 1px solid #ddd;
            ${showToolbar ? 'border-top: none;' : ''}
            min-height: 150px;
            padding: 12px;
            background: white;
            font-family: Arial, sans-serif;
            font-size: 14px;
            line-height: 1.4;
            outline: none;
            cursor: text;
            border-radius: ${showToolbar ? '0 0 4px 4px' : '4px'};
          "
        ></div>
        <div class="rte-counter" style="
          font-size: 12px;
          color: #666;
          text-align: right;
          padding: 4px 8px;
          border: 1px solid #ddd;
          border-top: none;
          background: #f8f9fa;
          border-radius: 0 0 4px 4px;
        ">0 / ${maxLength}</div>
      </div>
    `);
  }

  attach(element: any) {
    const refs = super.attach(element);
    const editor = this.refs.editor;
    const toolbar = this.refs.toolbar;
    const maxLength = this.component.maxLength || 5000;

    if (editor) {
      // Handle toolbar buttons
      if (toolbar) {
        const buttons = toolbar.querySelectorAll('.rte-btn');
        buttons.forEach((btn: any) => {
          this.addEventListener(btn, 'click', (e: Event) => {
            e.preventDefault();
            const command = btn.getAttribute('data-command');
            if (command) {
              document.execCommand(command, false, null);
              editor.focus();
              this.updateValue();
            }
          });
        });
      }

      // Handle focus
      this.addEventListener(editor, 'focus', () => {
        editor.style.borderColor = '#0077cc';
      });
      
      // Handle blur
      this.addEventListener(editor, 'blur', () => {
        editor.style.borderColor = '#ddd';
        this.updateValue();
      });
      
      // Handle input with character limit
      this.addEventListener(editor, 'input', () => {
        const textLength = editor.textContent?.length || 0;
        
        // Enforce character limit
        if (textLength > maxLength) {
          const text = editor.textContent || '';
          editor.textContent = text.substring(0, maxLength);
          
          // Move cursor to end
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(editor);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
        
        this.updateCounter();
        this.updateValue();
      });
      
      // Handle paste
      this.addEventListener(editor, 'paste', (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') || '';
        const currentLength = editor.textContent?.length || 0;
        const remainingLength = maxLength - currentLength;
        
        if (remainingLength > 0) {
          const textToInsert = text.substring(0, remainingLength);
          document.execCommand('insertText', false, textToInsert);
        }
        
        this.updateCounter();
        this.updateValue();
      });
      
      // Set initial value
      if (this.dataValue) {
        editor.innerHTML = this.dataValue;
      }
      
      this.updateCounter();
    }

    return refs;
  }

  updateCounter() {
    const editor = this.refs.editor;
    const container = editor?.parentElement;
    const counter = container?.querySelector('.rte-counter');
    
    if (counter && editor) {
      const textLength = editor.textContent?.length || 0;
      const maxLength = this.component.maxLength || 5000;
      counter.textContent = `${textLength} / ${maxLength}`;
      
      // Change color if approaching limit
      if (textLength > maxLength * 0.9) {
        counter.style.color = '#dc3545';
      } else if (textLength > maxLength * 0.8) {
        counter.style.color = '#ffc107';
      } else {
        counter.style.color = '#666';
      }
    }
  }

  getValue() {
    return this.dataValue || '';
  }

  setValue(value: any) {
    this.dataValue = value;
    if (this.refs.editor) {
      this.refs.editor.innerHTML = value || '';
      this.updateCounter();
    }
    return true;
  }
  
  updateValue() {
    if (this.refs.editor) {
      const newValue = this.refs.editor.innerHTML;
      if (this.dataValue !== newValue) {
        this.dataValue = newValue;
        this.triggerChange();
      }
    }
  }
}

// Register custom component
(Components as any).addComponent('richtexteditor', RichTextEditorComponent);